import { Lock } from 'lucide-react';
import type { Room } from '../../entities/room/types';
import { roomOnlineCount } from '../../entities/room/types';
import { useAppStore } from '../../app/appStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import type { MessageKey } from '../../shared/i18n/messages';
import { fmtInt, fmtMoney, fmtPercent } from '../../shared/lib/format';
import { roomDirectoryAccess, roomMinimumStake, roomRakebackPct } from './LobbyPage';

// Low to high — the list reads "start here, aim there".
const TIER_ORDER = ['bronze', 'silver', 'gold', 'diamond', 'black'];

/**
 * The rooms of one game, unfolded under its card in the hall (2026-09-09).
 * A flat list, nothing nested: every row carries its own tier badge, rows are
 * ordered by tier and then by who is inside, and the one room the player is
 * most likely to want has the only solid button. Locked rows say why in a
 * toast rather than with a dead button.
 */
export function GameRoomsInline({ rooms, vipLevel, walletBrl, guestMode, onEnter }: {
  rooms: Room[];
  vipLevel: number;
  walletBrl: number;
  guestMode: boolean;
  onEnter: (room: Room) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const showToast = useAppStore((state) => state.showToast);
  const tierOf = (room: Room) => String(room.tier || 'bronze').toLowerCase();
  const tierRank = (room: Room) => {
    const i = TIER_ORDER.indexOf(tierOf(room));
    return i === -1 ? TIER_ORDER.length : i;
  };
  const byCode = (a: Room, b: Room) => String(a.code || a.id).localeCompare(String(b.code || b.id));
  const byActivity = (a: Room, b: Room) =>
    roomOnlineCount(b) - roomOnlineCount(a)
    || Number(b.thisRoundBets || 0) - Number(a.thisRoundBets || 0)
    || byCode(a, b);
  const ordered = [...rooms].sort((a, b) => tierRank(a) - tierRank(b) || byActivity(a, b));
  // Room numbers follow the room code, so they do not shuffle as people move.
  const numberOf = (room: Room) =>
    rooms.filter((r) => tierOf(r) === tierOf(room)).sort(byCode).findIndex((r) => r.id === room.id) + 1;
  const recommended = [...rooms]
    .filter((room) => roomDirectoryAccess(room, guestMode, vipLevel, t).allowed)
    .sort((a, b) => byActivity(a, b)
      || Number(walletBrl >= roomMinimumStake(b)) - Number(walletBrl >= roomMinimumStake(a)))[0];

  function actOnRoom(room: Room) {
    const access = roomDirectoryAccess(room, guestMode, vipLevel, t);
    if (access.action === 'enter') onEnter(room);
    else if (access.action === 'signin') showToast(t('roomDirectorySignInPopup'), 'warn', { ms: 3600 });
    else if (access.action === 'vip') {
      showToast(t('roomDirectoryVipGatePopup')
        .replace('%vip%', String(Number(room.minVipLevel || 0)))
        .replace('%my%', String(vipLevel)), 'warn', { ms: 4000 });
    } else showToast(t('roomDirectoryUnavailable'), 'warn');
  }

  if (ordered.length === 0) return <p className="hn-rooms-empty">{t('roomDirectoryNoRooms')}</p>;

  return (
    <ul className="hn-rooms" data-hall-rooms-react="true">
      {ordered.map((room) => {
        const tier = tierOf(room);
        const access = roomDirectoryAccess(room, guestMode, vipLevel, t);
        const perk = roomRakebackPct(room);
        const minStake = roomMinimumStake(room);
        const maxStake = Number(room.perRoundMaxBrl || 0);
        return (
          <li key={room.id}>
            <button
              type="button"
              className={`hn-room tap${room.id === recommended?.id ? ' is-primary' : ''}${access.allowed ? '' : ' is-locked'}`}
              onClick={() => actOnRoom(room)}
              data-room-id={room.id}
              data-room-tier={tier}
            >
              <span className={`hn-tier is-${tier}`}>{t(`tier_${tier}` as MessageKey)}</span>
              <span className="hn-room-main">
                <span className="hn-room-name">
                  {t('roomDirectoryRoomN').replace('%n%', String(numberOf(room)))}
                  <i className="hn-room-online" data-online-count><b /> {fmtInt(roomOnlineCount(room))}</i>
                </span>
                <span className="hn-room-sub">
                  {minStake > 0 && maxStake > 0 ? `${fmtMoney(minStake)} – ${fmtMoney(maxStake)}` : ''}
                  {perk > 0 ? ` · ${t('roomDirectoryBackBadge').replace('%pct%', fmtPercent(perk))}` : ''}
                </span>
              </span>
              {access.action === 'enter' ? (
                <span className="hn-room-cta">{t('roomDirectoryPlay')}</span>
              ) : access.action === 'vip' ? (
                <span className="hn-room-lock"><Lock size={12} aria-hidden="true" />{t('roomDirectoryVipShort').replace('%vip%', String(Number(room.minVipLevel || 0)))}</span>
              ) : access.action === 'signin' ? (
                <span className="hn-room-lock"><Lock size={12} aria-hidden="true" />{t('login')}</span>
              ) : (
                <span className="hn-room-lock">{t('roomDirectoryUnavailable')}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
